import React from 'react';
import PropTypes from 'prop-types';
import styles from './SimpleTable.module.css';
import { Button as AugButton } from '../buttons';

/**
 * SimpleTable
 * - Reusable table wrapper that enforces header top/bottom borders and padding.
 * - Accepts an array of header labels and arbitrary tbody children.
 *
 * Props:
 * - headers: string[] | ReactNode[]
 * - children: ReactNode (rendered inside <tbody>)
 * - className?: string (appended to root table for external styling)
 */
export function SimpleTable({ headers = [], children, className = '', pagination = null }) {
  const renderPagination = () => {
    if (!pagination) return null;
    const { page, totalPages, hasNext, loading, onPageChange, onPrev, onNext } = pagination;
    const tp = totalPages || (hasNext ? page + 1 : page);

    // Do not render pagination when there is only one page
    if (totalPages) {
      if (totalPages <= 1) return null;
    } else {
      // Without totalPages, use heuristic: on first page and no next => single page
      if (page <= 1 && !hasNext) return null;
    }

    const addPage = (arr, p) => {
      arr.push(
        <button
          key={`page-${p}`}
          className={`${styles.pagerButtonNumber} ${styles.pagerButton} ${p === page ? styles.pagerButtonActive : styles.pagerButtonLink}`}
          disabled={p === page || loading}
          onClick={() => onPageChange && onPageChange(p)}
          aria-current={p === page ? 'page' : undefined}
        >
          {p}
        </button>
      );
    };
    const addEllipsis = (arr, k) => arr.push(<span key={k} className={styles.ellipsis}>…</span>);

    const items = [];
    // Window around current page (1 neighbor each side)
    const start = Math.max(1, page - 1);
    const end = Math.min(tp, page + 1);

    // Previous
    items.push(
      <div className={styles.pagerContainer} key="prev-container">
      <AugButton
        className={styles.pagerButton}
        textColor="var(--brand-as-neutral-950)"
        size="sm"
        iconLeft={{ name: 'arrows/keyboard_arrow_left' }}
        variant="ghost"
        key="prev"
        // className={styles.pagerButton}
        onClick={() => (onPrev ? onPrev() : onPageChange && onPageChange(page - 1))}
        disabled={page <= 1 || loading}
      >
        Previous
      </AugButton>
      </div>
    );

    // First page
    addPage(items, 1);
    if (start > 2) addEllipsis(items, 'start-ellipsis');

    for (let p = start; p <= end; p++) {
      if (p !== 1 && p !== tp) addPage(items, p);
    }

    if (end < tp - 1) addEllipsis(items, 'end-ellipsis');
    if (tp > 1) addPage(items, tp);

    // Next
    const nextDisabled = loading || (totalPages ? page >= totalPages : !hasNext);
    items.push(
      <AugButton
        className={`${styles.pagerButton} ${styles.pagerButtonLeft}`}
        textColor="var(--brand-as-neutral-950)"
        size="sm"
        iconRight={{ name: 'arrows/keyboard_arrow_right' }}
        variant="ghost"
        key="next"
        // className={styles.pagerButton}
        onClick={() => (onNext ? onNext() : onPageChange && onPageChange(page + 1))}
        disabled={nextDisabled}
        aria-label="Next page"
        title="Next"
      >
        Next
      </AugButton>
    );

    return (
      <div className={styles.pagination}>
        {items}
      </div>
    );
  };

  return (
    <>
      <table className={`${styles.table}${className ? ' ' + className : ''}`}>
        <thead className={styles.thead}>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className={`${styles.tbody}`}>{children}</tbody>
      </table>
      {renderPagination()}
    </>
  );
}

SimpleTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.node),
  children: PropTypes.node,
  className: PropTypes.string,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    totalPages: PropTypes.number,
    hasNext: PropTypes.bool,
    loading: PropTypes.bool,
    onPageChange: PropTypes.func.isRequired,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
  }),
};

export default SimpleTable;
